import React, { useState, useRef, useEffect } from 'react';

/**
 * Free-typing combobox for the Customer field.
 *
 * - `value` is the string currently in the input (typed or selected name).
 * - `onChange(text)` fires as the user types.
 * - `onCustomerSelect(customer)` fires when the user clicks a suggestion;
 *   parents can use this to look up addresses, set the customer_id, etc.
 * - Suggestions are filtered against the customer's display name
 *   (`getDisplayName(customer)`).
 */
const CustomerCombobox = ({
  customers = [],
  value = '',
  onChange,
  onCustomerSelect,
  getDisplayName,
  placeholder = 'Type a customer name...',
  disabled = false,
  invalid = false,
  testId = 'customer-combobox',
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const query = (value || '').toLowerCase().trim();
  const matches = customers
    .map((c) => ({ c, name: getDisplayName(c) }))
    .filter(({ name }) => !query || name.toLowerCase().includes(query))
    .slice(0, 50);

  return (
    <div ref={wrapperRef} className="flex-1 relative">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className={`border rounded px-3 py-2 w-full ${invalid ? 'border-red-500 bg-red-50' : 'border-gray-300'} ${disabled ? 'bg-gray-100' : ''}`}
        data-testid={`${testId}-input`}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg"
          data-testid={`${testId}-list`}
        >
          {matches.map(({ c, name }, idx) => (
            <li
              key={`cust-${idx}-${name}`}
              onMouseDown={(e) => {
                // mousedown (not click) so we don't lose focus before selection.
                e.preventDefault();
                onCustomerSelect?.(c);
                onChange?.(name);
                setOpen(false);
              }}
              className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-[#5B9BD5] hover:text-white"
              data-testid={`${testId}-option-${idx}`}
            >
              {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomerCombobox;
